import type { JarvisFetch } from "./api";

/**
 * Builds a fetch wrapper that resolves relative Jarvis paths against `baseUrl` and attaches the
 * bearer token, so callers can pass plain endpoint paths such as `/jarvis/send-message`.
 */
export function createJarvisFetch(baseUrl: string, authToken?: string): JarvisFetch {
  const resolveUrl = (input: globalThis.RequestInfo | URL): string => {
    if (typeof input === "string") {
      return input.startsWith("http")
        ? input
        : `${baseUrl}${input.startsWith("/") ? "" : "/"}${input}`;
    }
    return input instanceof URL ? input.toString() : input.url;
  };

  /* Only attach the bearer token when the resolved URL actually targets `baseUrl`, so an absolute
     URL pointing elsewhere never receives the user's token. */
  const isSameOriginAsBase = (url: string): boolean => {
    try {
      return new URL(url, baseUrl).origin === new URL(baseUrl).origin;
    } catch {
      return false;
    }
  };

  return async (input, init) => {
    const url = resolveUrl(input);

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (authToken && !headers.has("Authorization") && isSameOriginAsBase(url)) {
      headers.set("Authorization", `Bearer ${authToken}`);
    }

    if (input instanceof Request && !init) {
      /* Spreading a `Request` yields an empty object, because its fields live on the prototype as
         getters. Copy them across explicitly so a POST does not silently become a GET. */
      const isBodyless = input.method === "GET" || input.method === "HEAD";

      return fetch(url, {
        method: input.method,
        headers,
        body: isBodyless ? undefined : await input.clone().blob(),
        mode: input.mode,
        credentials: input.credentials,
        cache: input.cache,
        redirect: input.redirect,
        referrer: input.referrer,
        referrerPolicy: input.referrerPolicy,
        integrity: input.integrity,
        keepalive: input.keepalive,
        signal: input.signal,
      });
    }

    return fetch(url, { ...init, headers });
  };
}
